import type { CupRound } from '@/lib/competition/cupRounds';
import { type RoundKey, compareRoundKeys, roundKeyId, roundKeyOf } from '@/lib/competition/roundBuckets';

/** Ce que la page publique lit d'un match (JSON de calendar-export). */
export interface PublicCalendarMatchRef {
  game_day: number | null;
  match_date?: string | null;
  is_external_competition?: boolean | null;
  cup_round?: string | null;
  league_group: { kind?: string | null } | null;
}

/** Ligne cup_rounds renvoyée par calendar-export (`cup_rounds`). */
export interface PublicCupRoundRow {
  code: string;
  sort_order: number;
  name: string;
  name_ar: string;
  short_name: string;
  short_name_ar: string;
}

export function publicCupRoundsMap(rows: readonly PublicCupRoundRow[] | null | undefined): Map<string, CupRound> {
  return new Map(
    (rows ?? []).map((r) => [
      r.code,
      { code: r.code, sortOrder: r.sort_order, name: r.name, nameAr: r.name_ar, shortName: r.short_name, shortNameAr: r.short_name_ar },
    ]),
  );
}

/**
 * Sections de la page publique : une par journée de championnat (0 = sans journée),
 * une par tour de coupe — un match de coupe n'est jamais rangé sous « Journée 1 ».
 * Ordre chronologique par la date du premier match de la section (un calendrier
 * d'équipe mêle journées et tours) ; section sans date à la fin ; à date égale (ou
 * sans date), journées avant tours puis ordre des tas (journées croissantes, tours par
 * sort_order, « tour non précisé » en dernier).
 */
export function groupPublicCalendar<M extends PublicCalendarMatchRef>(
  matches: readonly M[],
  rounds: ReadonlyMap<string, CupRound>,
): Array<{ id: string; key: RoundKey; matches: M[] }> {
  const byId = new Map<string, { id: string; key: RoundKey; matches: M[]; firstDate: string | null }>();
  for (const m of matches) {
    const key = roundKeyOf({
      isExternalCompetition: m.is_external_competition,
      leagueGroup: m.league_group,
      cupRound: m.cup_round,
      gameDay: m.game_day,
    });
    const id = roundKeyId(key);
    let bucket = byId.get(id);
    if (!bucket) {
      bucket = { id, key, matches: [], firstDate: null };
      byId.set(id, bucket);
    }
    bucket.matches.push(m);
    const d = m.match_date ?? null;
    if (d && (bucket.firstDate === null || d < bucket.firstDate)) bucket.firstDate = d;
  }
  return Array.from(byId.values())
    .sort((a, b) => {
      if (a.firstDate !== b.firstDate) {
        if (a.firstDate === null) return 1;
        if (b.firstDate === null) return -1;
        return a.firstDate < b.firstDate ? -1 : 1;
      }
      return compareRoundKeys(a.key, b.key, rounds);
    })
    .map(({ id, key, matches: ms }) => ({ id, key, matches: ms }));
}
